import { NavigateNext } from "@mui/icons-material";
import { Breadcrumbs } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetOneWorkSpace } from "../../../services/WorkSpaceServices";
import { WorkSpaceUpdate } from "../../../model/WorkSpaceModel";
import { GetAllGroupsByInternalName } from "../../../services/GroupsServices";
import { Group } from "../../../model/GroupModel";
import { useUrlChangeListener } from "../../../hooks/useUrlChangeListener";
import { GetTreeByWorkspaceFolderIdAll } from "../../../services/Item/ItemServicesGet";
import { WorkSpaceItemRecursive } from "../../../model/ItemModel";
import { useTranslation } from "react-i18next";
import { BreadcrumbLink } from "./BreadcrumbLink";

interface IKeyDriveBreadcrumb {
  title: string;
  path: string;
}

export function KeyDriveBreadcrumb() {
  const location = useUrlChangeListener();
  const { nameOrg, idWorkspace, idGroup, idFolder } = useParams();
  const [workspace, setWorkspace] = useState<WorkSpaceUpdate>();
  const [groups, setGroups] = useState<Group[]>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const pathnames = useMemo(() => location.pathname.split('/').filter((x) => x), [location.pathname]);
  const excludeMapBreadcrumb = ["adminConsole", "wsallowed", "Item", "WorkSpace"];
  const path = useMemo(() => pathnames.filter(x => !excludeMapBreadcrumb.includes(x)), [pathnames]);
  
  const [dynamicNames, setDynamicNames] = useState<Record<string, string>>({});
  const [folderHierarchy, setFolderHierarchy] = useState<Record<string, WorkSpaceItemRecursive>>({});

  const listPathsCommon = useMemo((): IKeyDriveBreadcrumb[] => [
    { title: t("Sidebar.Home"), path: nameOrg ?? "" },
    { title: "Workspaces", path: "workspaces" },
    { title: t("AdminConsole.UsersAndGroups"), path: "usersAndGroups" },
    { title: t("AdminConsole.AccountAndSubscription"), path: "accountAndSubscription" },
    { title: t("AdminConsole.NewUser"), path: "newUser" },
    { title: t("AdminConsole.NewGroup"), path: "newGroup" },
    { title: t("Configuracoes.message"), path: "config" },
    { title: t("Sidebar.Dashboard"), path: "dashboard" },
    { title: t("MyData.Mydata"), path: "mydata" },
    { title: t("RecicleBin.title"), path: "recycleBin" },
    { title: "Resultados", path: "allResults" },
    { title: t("Sidebar.Groups"), path: "groups" },
    { title: t("Sidebar.Users"), path: "users" },
    { title: t("Upload.permission"), path: "Permission" },
    { title: t("Sidebar.Temporality"), path: "temporality" },
    { title: "Tipos de Documento", path: "documentTypes" },
    { title: t("Sidebar.ValidationRules"), path: "validationRule" },
    { title: t("Feed.Updates"), path: "tracker" },
    { title: "Arquivo", path: "file" },
    { title: t("Sidebar.Pendencies"), path: "UserPendencies" },
    { title: t("Upload.permission"), path: "PermissionUser" },
    { title: t("Sidebar.Preferences"), path: "preferences" },
    { title: t("New.female"), path: "new" },
    { title: t("AdminConsole.GuestUserAdd"), path: "newGuestUser" },
    { title: t("SharedItem.Title"), path: "SharedItem" },
    { title: t("AdminConsole.Plans"), path: "plans" },
  ], [t, nameOrg]);

  const toTitleCase = useCallback((str: string) => {
    return listPathsCommon.find(x => x.path === str)?.title ?? str;
  }, [listPathsCommon]);

  const addFolderName = useCallback((foldersNew: WorkSpaceItemRecursive[]) => {
    const newNames: Record<string, string> = {};
    const hierarchy: Record<string, WorkSpaceItemRecursive> = {};
    
    const processFolders = (folders: WorkSpaceItemRecursive[]) => {
      folders?.forEach(folder => {
        if (folder.id && folder.name) {
          newNames[folder.id] = folder.name;
          hierarchy[folder.id] = folder;
          if (folder.children?.length) {
            processFolders(folder.children);
          }
        }
      });
    };
    
    processFolders(foldersNew);
    setDynamicNames(newNames);
    setFolderHierarchy(hierarchy);
  }, []);

  useEffect(() => {
    if (idWorkspace) {
      Promise.all([
        GetOneWorkSpace(idWorkspace),
        GetTreeByWorkspaceFolderIdAll(idWorkspace)
      ])
      .then(([workspaceData, folderData]) => {
        if (workspaceData) setWorkspace(workspaceData);
        if (folderData) addFolderName(folderData);
      })
      .catch(err => console.error('Error loading workspace data:', err));
    }

    if (path[path.length - 1] === 'usersAndGroups' && nameOrg) {
      GetAllGroupsByInternalName(nameOrg)
        .then(groups => {setGroups(groups)})
        .catch(err => console.error('Error loading group:', err));
    }
  }, [idWorkspace, idGroup, addFolderName, path]);

    function getNameGroup() {
      if (idGroup && groups) {
        const foundGroup = groups.find(g => g.id === idGroup);
        if (foundGroup) {
          return foundGroup.name;
        }
      }
      return idGroup;
    }
    
  const buildFolderHierarchy = useCallback((folderId: string) => {
    const folderPath: string[] = [];
    const folderIds: string[] = [];
    let currentFolder: WorkSpaceItemRecursive | undefined = folderHierarchy[folderId];
    
    while (currentFolder) {
      folderPath.unshift(currentFolder.name!);
      folderIds.unshift(currentFolder.id!);
      currentFolder = currentFolder.fatherId ? folderHierarchy[currentFolder.fatherId] : undefined;
    }
    
    return { folderPath, folderIds };
  }, [folderHierarchy]);

  const renderBetterText = useCallback((input: string): string => {
    if (!input) return "";
    if (idGroup) return getNameGroup() || t("AdminConsole.Group");
    if (input === idWorkspace) return workspace?.name || input;    
    if (input === pathnames[pathnames.length - 1]) return dynamicNames[input] || input;
    return input;
  }, [idWorkspace, idGroup, workspace, pathnames, dynamicNames]);

  const breadcrumbItems = useMemo(() => {
    // Special case for adminConsole/workspaces/config route
    if (pathnames.includes("adminConsole") && pathnames.includes("workspaces") && pathnames.includes("config") && idWorkspace) {
      return [
        <BreadcrumbLink
          key="home"
          onClick={() => navigate(`/${nameOrg}/adminConsole`)}
          text={t("Sidebar.Home")}
        />,
        <BreadcrumbLink
          key="workspaces"
          onClick={() => navigate(`/${nameOrg}/adminConsole/workspaces`)}
          text="Workspaces"
        />,
        <BreadcrumbLink
          key="editing"
          onClick={() => {}}
          text={`Editando ${workspace?.name || idWorkspace}`}
        />
      ];
    }

    // Special handling for adminConsole routes
    if (pathnames.includes("adminConsole")) {
      const items: JSX.Element[] = [];
      
      // Add Admin Console home
      items.push(
        <BreadcrumbLink
          key="adminConsole"
          onClick={() => navigate(`/${nameOrg}/adminConsole`)}
          text={t("Sidebar.Home")}
        />
      );
      
      // Add subsequent adminConsole path items
      const adminIndex = pathnames.indexOf("adminConsole");
      const adminPath = pathnames.slice(adminIndex + 1);
      
      adminPath.forEach((value, index) => {
        const isLast = index === adminPath.length - 1;
        const to = `/${nameOrg}/adminConsole/${adminPath.slice(0, index + 1).join('/')}`;

        let displayText = toTitleCase(value);

        if (value == idGroup) {
          displayText = renderBetterText('toTitleCase(value)');
        }
        else if (value === idWorkspace && workspace?.name) {
          displayText = renderBetterText(toTitleCase(value));
        }

        items.push(
          <BreadcrumbLink
            key={`admin-${index}`}
            onClick={isLast ? () => {} : () => navigate(to)}
            text={displayText}
          />
        );
      });
      
      return items;
    }

    const currentFolderId = pathnames[pathnames.length - 1];
    const isInFolder = folderHierarchy[currentFolderId];
    
    if (isInFolder?.fatherId) {
      const { folderPath, folderIds } = buildFolderHierarchy(currentFolderId);
      const items: JSX.Element[] = [];
      
      // Add regular path items except the last folder
      path.slice(0, -1).forEach((value, index) => {
        let to = `/${path.slice(0, index + 1).join('/')}`;
        if (path[index-1] === nameOrg && path[index] === idWorkspace) {
          to = `/${nameOrg}/wsallowed/${idWorkspace}`;
        }
        
        items.push(
          <BreadcrumbLink
            key={`path-${index}`}
            onClick={() => navigate(to)}
            text={renderBetterText(toTitleCase(value))}
          />
        );
      });
      
      // Add folder hierarchy
      folderPath.forEach((folderName, index) => {
        const folderId = folderIds[index];
        const folderTo = `/${nameOrg}/wsallowed/${idWorkspace}/${folderId}`;
        
        items.push(
          <BreadcrumbLink
            key={`folder-${folderId}`}
            onClick={() => navigate(folderTo)}
            text={folderName}
          />
        );
      });
      
      return items;
    }
    
    if (pathnames[pathnames.length - 1] === "PermissionUser" && idFolder && folderHierarchy[idFolder]) {
      const { folderPath, folderIds } = buildFolderHierarchy(idFolder);
      const items: JSX.Element[] = [];

      path.slice(0, -2).forEach((value, index) => {
      let to = `/${path.slice(0, index + 1).join('/')}`;
      if (path[index-1] === nameOrg && path[index] === idWorkspace) {
        to = `/${nameOrg}/wsallowed/${idWorkspace}`;
      }
      
      items.push(
        <BreadcrumbLink
        key={`path-${index}`}
        onClick={() => navigate(to)}
        text={renderBetterText(toTitleCase(value))}
        />
      );
      });

      folderIds.forEach((folderId, index) => {
      items.push(
        <BreadcrumbLink
        key={`folder-${folderId}`}
        onClick={() => navigate(`/${nameOrg}/wsallowed/${idWorkspace}/${folderId}`)}
        text={folderPath[index]}
        />
      );
      });

      items.push(
      <BreadcrumbLink
        key="permission-user"
        onClick={() => {}}
        text={toTitleCase("PermissionUser")}
      />
      );

      return items;
    }
    // Default behavior for non-folder paths
    return path.map((value, index) => {
      const isLast = index === path.length - 1;
      let to = `/${path.slice(0, index + 1).join('/')}`;

      // Handle special route transformations
      if (path[index-1] === nameOrg && path[index] === idWorkspace) {
        to = `/${nameOrg}/wsallowed/${idWorkspace}`;
      }
      // if (pathnames[index-1] === nameOrg && pathnames[index] === "WorkSpace") {
      //   to = `/${nameOrg}/wsallowed`;
      // }

      return (
        <BreadcrumbLink
          key={`default-${index}`}
          onClick={isLast ? () => {} : () => navigate(to)}
          text={renderBetterText(toTitleCase(value))}
        />
      );
    });
  }, [pathnames, folderHierarchy, buildFolderHierarchy, path, nameOrg, idWorkspace, idGroup, navigate, renderBetterText, toTitleCase]);

  return (
    <Breadcrumbs 
      separator={<NavigateNext style={{ opacity: "0.5" }} fontSize="small" />} 
      aria-label="breadcrumb" 
      style={{ color: "#22638D" }}
    >
      {breadcrumbItems}
    </Breadcrumbs>
  );
}